import { forwardRef } from 'react';
import type {
  ChangeEvent,
  DetailedHTMLProps,
  SelectHTMLAttributes,
  ReactNode,
} from 'react';
import { css } from '@emotion/react';
import type { Interpolation, Theme } from '@emotion/react';
import styled from '@emotion/styled';
import isPropValid from '@emotion/is-prop-valid';

import { spacer, transition, color } from '@tokens';
import { Icon } from '@components/Icon/Icon';
import type { IconName } from '@components/Icon/BaseIcon';
import { baseInput } from '../styles';

export interface SelectProps
  extends Omit<
    DetailedHTMLProps<
      SelectHTMLAttributes<HTMLSelectElement>,
      HTMLSelectElement
    >,
    'required' | 'size' | 'disabled' | 'multiple' | 'ref'
  > {
  /**
   * The unique ID (attribute) of the html select element.
   */
  id?: string;
  /**
   * Name (attribute) of the html select element.
   */
  name?: string;
  /**
   * Size of the select. 3 sizes, to result in 32/40/56 px height.
   */
  size?: 's' | 'm' | 'l';
  /**
   * Icon to show on the left of the select.
   */
  icon?: IconName;
  /**
   * Specifies if the select is disabled.
   */
  isDisabled?: boolean;
  /**
   * Specifies if the select is invalid. When set to true, we'll render the select in invalid state.
   */
  isInvalid?: boolean;
  /**
   * Specifies if the select is required.
   */
  isRequired?: boolean;
  /**
   * Callback when the select value is changed.
   */
  onChange?: (event: ChangeEvent<HTMLSelectElement>) => void;
  /**
   * Styles override for the select element.
   */
  selectStyles?: Interpolation<Theme>;
  /**
   * Styles override for the select container.
   */
  className?: string;
  /**
   * Options for the select.
   */
  children: ReactNode;
}

interface StyledSelectProps {
  isInvalid: boolean;
  selectSize: 's' | 'm' | 'l';
  isIconPresent: boolean;
  readOnly: false;
}

const SELECT_HEIGHT = {
  s: 32,
  m: 40,
  l: 56,
};

const StyledSelect = styled('select', {
  shouldForwardProp: (prop: string) => isPropValid(prop),
})<StyledSelectProps>`
  ${baseInput};
  height: ${({ selectSize }) => SELECT_HEIGHT[selectSize]}px;
  padding: 0;
  padding-left: ${({ isIconPresent }) =>
    isIconPresent ? spacer.x5 : spacer.x2};
  padding-right: ${({ isInvalid }) => (isInvalid ? spacer.x8 : spacer.x5)};
  ${transition()};
  appearance: none;
  text-overflow: ellipsis;

  > option {
    color: ${color.text_default};
  }

  &:invalid,
  > option[disabled] {
    color: ${color.text_disabled};
  }

  > option[disabled] {
    cursor: not-allowed;
  }
`;

const SelectContainer = styled.div`
  position: relative;
`;

const InputIcon = styled(Icon)`
  position: absolute;
  top: 0;
  bottom: 0;
  margin: auto;
  pointer-events: none;
`;

/**
 * A select allows users to select an option from multiple ones
 *
 * {@link https://zeroheight.com/76c439891/p/90540d-select/b/314272 Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-select--basic-select Code documentation}
 *
 * @example
 * <Select>
 *   <option value="foo">Foo</option>
 *   <option value="bar">Bar</option>
 * </Select>
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      isDisabled,
      isInvalid,
      isRequired,
      selectStyles,
      size = 'm',
      icon,
      children,
      className,
      ...props
    }: SelectProps,
    ref,
  ): JSX.Element => (
    <SelectContainer className={className}>
      <StyledSelect
        ref={ref}
        {...props}
        isIconPresent={!!icon}
        selectSize={size}
        isInvalid={isInvalid}
        readOnly={false}
        disabled={isDisabled}
        required={isRequired}
        css={selectStyles}
      >
        {children}
      </StyledSelect>

      {!!icon && (
        <InputIcon
          icon={icon}
          size="m"
          color="text_disabled"
          css={css`
            left: ${spacer.x2};
          `}
        />
      )}
      {isInvalid && (
        <InputIcon
          icon="warning_alt"
          size="m"
          color="text_danger"
          css={css`
            right: ${spacer.x5};
          `}
        />
      )}
      {!isDisabled && (
        <InputIcon
          icon="caret_down_alt"
          size="m"
          color="text_light"
          css={css`
            right: ${spacer.x2};
          `}
        />
      )}
    </SelectContainer>
  ),
);

Select.displayName = 'Select';

Select.defaultProps = {
  size: 'm',
  isDisabled: false,
  isRequired: false,
  isInvalid: false,
};
